import { TierSchema } from '@developerz/domain';
import { Kicker } from '@developerz/ui';
import { A } from '@solidjs/router';
import { createMemo, createSignal, createUniqueId, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AdminAccountDetail } from '../lib/admin';
import { type OverrideResult, type OverrideWrite, sendOverride } from '../lib/admin-overrides';
import { grantSubjectHref } from '../lib/adminGrants';
import { createConfirmGate } from '../lib/confirm-gate';
import type { OperatorRefusal } from '../lib/operator-credential';
import { money, moneyFromDecimal } from '../lib/time';
import { AdminMemberRole } from './AdminMemberRole';
import { tierLabel } from './BillingPlanCard';
import { ConfirmDialog } from './ConfirmDialog';
import { EmptyState } from './EmptyState';
import { Field } from './Field';
import { OperatorRefusalAlert } from './OperatorRefusalAlert';
import { Panel } from './Panel';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

// Admin org detail + edit controls. The writes are the operator overrides' REST
// twins (`PUT /v1/admin/accounts/:id/{tier,seats,spend-cap}`, `lib/admin-overrides.ts`),
// and each body requires `confirm: true`, so Save ASKS first, the dialog itemises every
// change it will send (old → new), and `confirm: true` leaves only from its
// action (plan 101 slice 17 rows 17-1 / 17-16).
//
// THE REFUSAL OUTLIVES THE RE-READ. Each override rides an `admin:accounts`
// operator credential (#5724). The API's own reason is shown as it came, and it
// is held by the CALLER (`refusal` / `onRefusal`, the loader keyed by account):
// after any attempt that sent a write `onMutated` re-reads (a refused tier beside
// a landed seat count still changed the account), the re-read rebuilds this
// component, and a refusal held here was lost with it (review of row 17-1). A
// DOOR refusal is held by the caller too (`doorRefusal` / `onDoorRefusal`): the
// changes share one issuance, but a 401 on one write re-asks the door for that
// write alone, so the door can refuse one change after another has landed.
//
// STRIPE OWNS TIER AND SEATS when `stripe_seat_subscription_id` is a string
// (#5725: their overrides answer 409, and the next webhook would revert them).
// Both show read-only, with the reason and the grants page addressed to this
// account (#6425); the spend cap stays editable.
//
// ONLY A REAL STRING COUNTS (`isStripeBilled`, both call sites). `null` and a
// MISSING field both get the editable form: the API and the dashboard ship as
// separate images, so a dashboard ahead of its API (or an API rolled back
// alone) reads a body without the field. The server is the arbiter — a stray
// override still gets the 409, whose `detail` shows inline — whereas a false
// "billed through Stripe" during a skew would misdirect the operator on every org.
//
// `draftChanges` also takes the account's own tier and seats when Stripe owns
// them. A SECOND LINE OF DEFENCE, not today's path: `<AdminOrgDetailLoader/>`
// rebuilds this detail on every re-read (`<QueryBoundary/>` re-runs its
// children), seeding the signals from the new account. The guard is for a future
// caller that swaps `account` under a mounted page with an edit still typed.
//
// THE MEMBER LIST CHANGES ROLES (#5727): each row is `<AdminMemberRole/>`, whose
// success re-reads through `onMutated` like the overrides.
//
// NO BOX FAILS OPEN (row 17-11 + review). The spend cap is a TEXT box this
// parses: a `type="number"` box reads an invalid entry ("1e", a lone "-") as
// EMPTY, and empty once meant `null`, the route's "no cap" — the unbudgeted
// state metering reads as a zero used-fraction. `null` is sent only from the
// explicit "No spending cap" box; anything else that is not a dollar amount
// above 0 is refused here (the route's bound is `> 0`). A cleared seats
// box is refused too: `Number('')` is `0`, and an emptied box is not "zero seats".

/** The form as typed. */
type FormDraft = {
  readonly tier: string;
  readonly seats: string;
  readonly noCap: boolean;
  readonly cap: string;
};

/** One override the confirm will send, with the line that names it. */
type Change = { readonly write: OverrideWrite; readonly line: string };

/** The changes a draft asks for, or the field it cannot be sent over. */
type Draft =
  | { readonly kind: 'ok'; readonly changes: readonly Change[] }
  | { readonly kind: 'invalid_seats' }
  | { readonly kind: 'invalid_cap' };

/** A dollar amount above 0, or null: plain decimal digits only (no "1e3", no sign). */
function parseCap(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n > 0 ? n : null;
}

const capLabel = (cap: number | string | null): string =>
  cap === null
    ? t('admin.spend_cap_none')
    : typeof cap === 'number'
      ? money(cap)
      : moneyFromDecimal(cap);

/** Stripe owns tier and seats: a real subscription id only, never a missing field (see the header). */
const isStripeBilled = (account: AdminAccountDetail): boolean =>
  typeof account.stripe_seat_subscription_id === 'string';

function draftChanges(account: AdminAccountDetail, typed: FormDraft): Draft {
  const form: FormDraft = isStripeBilled(account)
    ? { ...typed, tier: account.tier, seats: String(account.seats) }
    : typed;
  const seatsText = form.seats.trim();
  const seats = Number(seatsText);
  if (!/^\d+$/.test(seatsText) || !Number.isSafeInteger(seats)) return { kind: 'invalid_seats' };
  const cap = form.noCap ? null : parseCap(form.cap);
  if (!form.noCap && cap === null) return { kind: 'invalid_cap' };

  const id = account.id;
  const changes: Change[] = [];
  // The select offers only `TierSchema.options`, so a changed value always parses.
  const tier = TierSchema.safeParse(form.tier);
  if (form.tier !== account.tier && tier.success) {
    changes.push({
      write: { action: 'set_tier', account_id: id, tier: tier.data, confirm: true },
      line: t('admin.change_tier', { from: tierLabel(account.tier), to: tierLabel(tier.data) }),
    });
  }
  if (seats !== account.seats) {
    changes.push({
      write: { action: 'set_seats', account_id: id, seats, confirm: true },
      line: t('admin.change_seats', { from: account.seats, to: seats }),
    });
  }
  const currentCap = account.spend_cap_usd === null ? null : Number(account.spend_cap_usd);
  if (cap !== currentCap) {
    changes.push({
      write: { action: 'set_spend_cap', account_id: id, spend_cap_usd: cap, confirm: true },
      line: t('admin.change_cap', { from: capLabel(account.spend_cap_usd), to: capLabel(cap) }),
    });
  }
  return { kind: 'ok', changes };
}

export function AdminOrgDetail(props: {
  account: AdminAccountDetail;
  token: string | null;
  /** The last attempt's refusal, held by the caller across the re-read. */
  refusal: string | null;
  onRefusal: (message: string | null) => void;
  /** The last attempt's door refusal, held by the caller across the re-read. */
  doorRefusal: OperatorRefusal | null;
  onDoorRefusal: (refusal: OperatorRefusal | null) => void;
  onMutated: () => void;
}): JSX.Element {
  const [tier, setTier] = createSignal(props.account.tier);
  const [seats, setSeats] = createSignal(String(props.account.seats));
  const [noCap, setNoCap] = createSignal(props.account.spend_cap_usd === null);
  const [cap, setCap] = createSignal(props.account.spend_cap_usd ?? '');
  const [busy, setBusy] = createSignal(false);
  const [invalid, setInvalid] = createSignal<'invalid_seats' | 'invalid_cap' | null>(null);
  const membersId = createUniqueId();
  const stripeBilled = (): boolean => isStripeBilled(props.account);

  const ask = (): void => {
    props.onRefusal(null);
    props.onDoorRefusal(null);
    const draft = draftChanges(props.account, {
      tier: tier(),
      seats: seats(),
      noCap: noCap(),
      cap: cap(),
    });
    if (draft.kind !== 'ok') {
      setInvalid(draft.kind);
      return;
    }
    setInvalid(null);
    if (draft.changes.length > 0) asking.ask(draft.changes);
  };

  const save = async (changes: readonly Change[]): Promise<void> => {
    setBusy(true);
    const outcomes = await Promise.all(changes.map((c) => sendOverride(c.write, props.token)));
    setBusy(false);
    const results: OverrideResult[] = [];
    for (const o of outcomes) {
      if (o.kind === 'ok') results.push(o.value);
      else props.onDoorRefusal(o.kind);
    }
    if (results.length === 0) return;
    const failed = results.find((r): r is Extract<OverrideResult, { ok: false }> => !r.ok);
    if (failed) props.onRefusal(failed.text);
    props.onMutated();
  };

  // Save only ASKS; the writes leave from the dialog's action (`createConfirmGate`).
  const asking = createConfirmGate<readonly Change[]>({ act: save });
  // The LAST asked changes: `pending()` empties when the save settles, and the
  // dialog keeps rendering through its close animation.
  const asked = createMemo<readonly Change[]>((last) => asking.pending() ?? last, []);

  return (
    <Panel title={props.account.github_login}>
      <div class="admin-detail">
        <Show when={stripeBilled()}>
          <dl class="facts">
            <div>
              <dt>{t('admin.field_tier')}</dt>
              <dd>{tierLabel(props.account.tier)}</dd>
            </div>
            <div>
              <dt>{t('admin.field_seats')}</dt>
              <dd class="facts__num">{props.account.seats}</dd>
            </div>
          </dl>
          <p class="field__desc">{t('admin.stripe_billed_reason')}</p>
          <div>
            <A
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              href={grantSubjectHref({
                kind: 'account',
                id: props.account.id,
                label: props.account.github_login,
              })}
            >
              {t('admin.stripe_billed_grants')}
            </A>
          </div>
        </Show>
        {/* A plain container, NOT a <form>: a real <form> as async-detail content
          beside the list <For/> trips a happy-dom template-walk crash. Save
          fires on the button. */}
        <div class="field-group">
          <Show when={!stripeBilled()}>
            <Field label={t('admin.field_tier')} id="admin-org-tier">
              {(a11y) => (
                <Select
                  a11y={a11y}
                  label={t('admin.field_tier')}
                  block
                  value={tier()}
                  disabled={busy()}
                  options={TierSchema.options.map((name) => ({
                    value: name,
                    label: tierLabel(name),
                  }))}
                  onChange={setTier}
                />
              )}
            </Field>
            <Field
              label={t('admin.field_seats')}
              id="admin-org-seats"
              error={invalid() === 'invalid_seats' ? t('admin.seats_required') : null}
            >
              {(a11y) => (
                <input
                  {...a11y}
                  class="input"
                  type="number"
                  min="0"
                  step="1"
                  value={seats()}
                  disabled={busy()}
                  onInput={(e) => setSeats(e.currentTarget.value)}
                />
              )}
            </Field>
          </Show>
          <Field
            label={t('admin.field_spend_cap')}
            id="admin-org-spend-cap"
            error={invalid() === 'invalid_cap' ? t('admin.cap_invalid') : null}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                inputmode="decimal"
                placeholder={noCap() ? t('admin.spend_cap_none') : ''}
                value={noCap() ? '' : cap()}
                disabled={busy() || noCap()}
                onInput={(e) => setCap(e.currentTarget.value)}
              />
            )}
          </Field>
          <label class="admin-detail__check">
            <input
              type="checkbox"
              checked={noCap()}
              disabled={busy()}
              onChange={(e) => setNoCap(e.currentTarget.checked)}
            />
            {t('admin.spend_cap_none_label')}
          </label>
          <div>
            <Button size="sm" disabled={busy()} onClick={ask}>
              {t('admin.save_changes')}
            </Button>
          </div>
        </div>

        <OperatorRefusalAlert refusal={props.doorRefusal} />
        {/* The API's own reason, as it came: it names the missing scope itself. */}
        <Show when={props.refusal}>
          {(msg) => (
            <p class="field__error" role="alert">
              {msg()}
            </p>
          )}
        </Show>

        <h3 id={membersId}>
          <Kicker as="span">{t('admin.members_heading')}</Kicker>
        </h3>
        <Show
          when={props.account.members.length > 0}
          fallback={<EmptyState message={t('admin.members_empty')} />}
        >
          <ul class="row-list" aria-labelledby={membersId}>
            <For each={props.account.members}>
              {(member) => (
                <li class="row-list__row">
                  <span class="row-list__lead">{member.login}</span>
                  <AdminMemberRole
                    accountId={props.account.id}
                    userId={member.user_id}
                    login={member.login}
                    org={props.account.github_login}
                    memberRole={member.role}
                    token={props.token}
                    onChanged={props.onMutated}
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
      <ConfirmDialog
        open={asking.pending() !== null}
        title={t('admin.org_save_title', { login: props.account.github_login })}
        desc={t('admin.org_save_desc')}
        cancelLabel={t('admin.confirm_cancel')}
        actionLabel={t('admin.org_save_confirm')}
        onCancel={asking.cancel}
        onConfirm={() => void asking.confirm()}
      >
        <ul class="confirm__changes">
          <For each={asked()}>{(change) => <li>{change.line}</li>}</For>
        </ul>
      </ConfirmDialog>
    </Panel>
  );
}
